import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

const NETWORKS = [
  { key: "SENDTOO_NETWORK", label: "Sendtoo Network" },
  { key: "DELHIVERY", label: "Delhivery" },
  { key: "BLUEDART", label: "Bluedart" },
];

export default function NetworkAssignments() {
  const [activeNetwork, setActiveNetwork] = useState("SENDTOO_NETWORK");
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveAwb, setMoveAwb] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchShipments(); setSelected(null); }, [activeNetwork]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v2/assignments/by-network?network=${activeNetwork}`);
      setShipments(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const filteredShipments = shipments.filter((s) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (s.order_id || "").toLowerCase().includes(q) ||
      (s.awb_number || "").toLowerCase().includes(q) ||
      (s.awb || "").toLowerCase().includes(q) ||
      (s.tracking_id || "").toLowerCase().includes(q);
  });

  const submitMove = async () => {
    if (!moveTarget) return toast.error("Select a target network");
    if (moveTarget !== "OWN_NETWORK" && !moveTarget.startsWith("DELHIVERY") && !moveAwb.trim()) return toast.error("Enter the AWB for the new network");
    if (!moveReason.trim()) return toast.error("Enter a reason for the move");
    setSubmitting(true);
    try {
      await api.post(`/v2/assignments/${selected.id}/move-network`, {
        new_carrier: moveTarget,
        new_awb: moveAwb || undefined,
        reason: moveReason,
      });
      toast.success("Shipment moved");
      setSelected(null);
      setMoveTarget("");
      setMoveAwb("");
      setMoveReason("");
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Assignments by Network</h1>
          <p className="text-slate-500 text-sm mt-0.5">Browse shipments by carrier, reassign, or move networks.</p>
        </div>

        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Order ID or AWB..."
          className="w-full max-w-sm px-3 py-2 border border-slate-200 rounded-lg text-sm" />

        <div className="flex gap-2 border-b border-slate-200">
          {NETWORKS.map((n) => (
            <button key={n.key} onClick={() => setActiveNetwork(n.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeNetwork === n.key ? "border-brand-500 text-brand-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {n.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : filteredShipments.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">{search ? "No matching shipments." : "No shipments on this network."}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Order ID", "AWB", "Customer", "From", "To", "Status"].map((h) => (
                        <th key={h} className="px-3 py-2 text-xs font-bold text-slate-400 uppercase text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredShipments.map((s) => (
                      <tr key={s.id} onClick={() => setSelected(s)}
                        className={`cursor-pointer hover:bg-slate-50 ${selected?.id === s.id ? "bg-brand-50" : ""} ${s.is_cancelled ? "opacity-60" : ""}`}>
                        <td className={`px-3 py-2 text-xs font-semibold text-brand-600 ${s.is_cancelled ? "line-through text-red-500" : ""}`}>{s.order_id}</td>
                        <td className={`px-3 py-2 text-xs font-mono ${s.is_cancelled ? "line-through text-red-500" : "text-slate-600"}`}>{s.awb_number || s.awb || s.tracking_id || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{s.customer_name || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{s.origin_city || s.origin?.city}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{s.dest_city || s.destination?.city}</td>
                        <td className="px-3 py-2 text-xs">
                          {s.is_cancelled ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">Cancelled</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">{s.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            {!selected ? (
              <p className="text-sm text-slate-400 text-center py-8">Select a shipment to view details or move it.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading font-semibold text-slate-800">{selected.order_id}</h3>
                  <p className="text-xs text-slate-500 font-mono mt-1">{selected.awb_number || selected.awb || selected.tracking_id}</p>
                  {selected.is_cancelled && (
                    <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">Cancelled on carrier side</span>
                  )}
                </div>
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Move to a different network</p>
                  <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm">
                    <option value="">Select network...</option>
                    <option value="OWN_NETWORK">Sendtoo Network</option>
                    <option value="DELHIVERY_B2C">Delhivery B2C</option>
                    <option value="DELHIVERY_B2B">Delhivery B2B</option>
                    <option value="BLUEDART">Bluedart</option>
                  </select>
                  {moveTarget && moveTarget !== "OWN_NETWORK" && !moveTarget.startsWith("DELHIVERY") && (
                    <input value={moveAwb} onChange={(e) => setMoveAwb(e.target.value)} placeholder="New carrier AWB"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                  )}
                  {moveTarget && moveTarget.startsWith("DELHIVERY") && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5">
                      No AWB needed here — after moving, use the "Manifest" button on the shipment to trigger Delhivery manifestation.
                    </p>
                  )}
                  <textarea value={moveReason} onChange={(e) => setMoveReason(e.target.value)} placeholder="Reason for moving..."
                    rows={2} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                  <button onClick={submitMove} disabled={submitting}
                    className="w-full px-3 py-2 bg-brand-500 text-white text-sm font-semibold rounded hover:bg-brand-600 disabled:opacity-50">
                    {submitting ? "Moving..." : "Confirm Move"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
